import React, { useState } from 'react';
import { useOrganizations, useCreateOrganization, useUpdateOrganization, useDeleteOrganization, useTestOrganizationIMAP, useTestIMAPConnection, useTestSMTPConnection, useTestOrganizationSMTP } from '@/hooks/useOrganizations';
import { type Organization, type CreateOrganizationData, type UpdateOrganizationData } from '@/services/organizations.service';
import {
  Plus,
  Trash2,
  FlaskConical,
  Save,
  X,
  Check,
  Mail,
  Server,
  Eye,
  EyeOff,
  AlertTriangle,
  CheckCircle,
  Loader2,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import { Switch } from './ui/switch';
import OrganizationDataTable from './OrganizationDataTable';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { formatDate } from '@/lib/format';
import { useTranslation } from 'react-i18next';

export const OrganizationsSection: React.FC = () => {
  const { t } = useTranslation();
  const { data, isLoading, error } = useOrganizations();
  const organizations = data?.data ?? [];
  const organizationCount = organizations.length;
  const activeCount = organizations.filter((org) => org.is_active).length;
  const inactiveCount = Math.max(0, organizationCount - activeCount);
  
  const createOrganization = useCreateOrganization();
  const updateOrganization = useUpdateOrganization();
  const deleteOrganization = useDeleteOrganization();
  const testOrganizationIMAP = useTestOrganizationIMAP();
  const testIMAPConnection = useTestIMAPConnection();
  const testSMTPConnection = useTestSMTPConnection();
  const testOrganizationSMTP = useTestOrganizationSMTP();

  // Custom test handler to show results in UI
  const handleIMAPTest = async (config: any, testKey: string) => {
    try {
      const result = await testIMAPConnection.mutateAsync(config);
      setTestResults(prev => ({ ...prev, [testKey]: result }));
    } catch (error) {
      setTestResults(prev => ({
        ...prev,
        [testKey]: {
          success: false,
          error: 'Test fehlgeschlagen',
          connected: false,
          authenticated: false
        }
      }));
    }
  };

  // Custom SMTP test handler to show results in UI
  const handleSMTPTest = async (config: any, testKey: string) => {
    try {
      const result = await testSMTPConnection.mutateAsync(config);
      setTestResults(prev => ({ ...prev, [testKey]: result }));
    } catch (error) {
      setTestResults(prev => ({
        ...prev,
        [testKey]: {
          success: false,
          error: 'Test fehlgeschlagen',
          connected: false,
          authenticated: false
        }
      }));
    }
  };

  const [newOrgData, setNewOrgData] = useState<CreateOrganizationData>({
    name: '',
    domain: '',
    support_email: '',
    is_warehouse: false,
    email_config: {
      imap: {
        host: '',
        port: 993,
        secure: true,
        user: '',
        password: ''
      },
      smtp: {
        host: '',
        port: 587,
        secure: false,
        user: '',
        password: ''
      }
    }
  });

  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [editData, setEditData] = useState<UpdateOrganizationData>({});
  const [showPasswords, setShowPasswords] = useState<{[key: string]: boolean}>({});
  const [testResults, setTestResults] = useState<{[key: string]: any}>({});
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [testingOrgs, setTestingOrgs] = useState<Set<string>>(new Set());

  const handleCreateOrganization = async () => {
    try {
      await createOrganization.mutateAsync(newOrgData);
      setNewOrgData({
        name: '',
        domain: '',
        support_email: '',
        is_warehouse: false,
        email_config: {
          imap: { host: '', port: 993, secure: true, user: '', password: '' },
          smtp: { host: '', port: 587, secure: false, user: '', password: '' }
        }
      });
      setIsCreateDialogOpen(false);
    } catch (error) {
      console.error('Failed to create organization:', error);
    }
  };

  const handleUpdateOrganization = async () => {
    if (!editingOrg) return;
    try {
      await updateOrganization.mutateAsync({ id: editingOrg.id, data: editData });
      setEditingOrg(null);
      setEditData({});
      setIsEditDialogOpen(false);
    } catch (error) {
      console.error('Failed to update organization:', error);
    }
  };

  const handleDeleteOrganization = async (orgId: string) => {
    try {
      await deleteOrganization.mutateAsync(orgId);
    } catch (error) {
      console.error('Failed to delete organization:', error);
    }
  };

  const handleTestIMAP = async (org: Organization) => {
    setTestingOrgs(prev => new Set(prev).add(org.id));
    try {
      await testOrganizationIMAP.mutateAsync(org);
    } finally {
      setTestingOrgs(prev => {
        const newSet = new Set(prev);
        newSet.delete(org.id);
        return newSet;
      });
    }
  };

  const handleTestNewOrgIMAP = async () => {
    await handleIMAPTest(newOrgData.email_config.imap, 'create-imap');
  };

  const handleTestNewOrgSMTP = async () => {
    await handleSMTPTest(newOrgData.email_config.smtp, 'create-smtp');
  };

  const handleTestEditOrgSMTP = async () => {
    if (editData.email_config?.smtp) {
      await handleSMTPTest(editData.email_config.smtp, `edit-smtp-${editingOrg?.id}`);
    }
  };

  const togglePasswordVisibility = (fieldId: string) => {
    setShowPasswords(prev => ({
      ...prev,
      [fieldId]: !prev[fieldId]
    }));
  };

  // Test result component
  const TestResult = ({ testKey, className = "" }: { testKey: string, className?: string }) => {
    const result = testResults[testKey];
    if (!result) return null;

    const baseClasses = result.success
      ? 'border-emerald-200 bg-emerald-50/70 text-emerald-700'
      : 'border-rose-200 bg-rose-50/70 text-rose-700';

    return (
      <div className={`mt-3 rounded-2xl border px-4 py-3 text-sm ${baseClasses} ${className}`}>
        <div className="flex items-center gap-2 font-medium">
          {result.success ? (
            <CheckCircle className="h-4 w-4 text-emerald-500" />
          ) : (
            <AlertTriangle className="h-4 w-4 text-rose-500" />
          )}
          <span>
            {result.success ? 'Test erfolgreich' : 'Test fehlgeschlagen'}
          </span>
        </div>
        <div className="mt-2 space-y-1 text-xs text-slate-600">
          {result.success ? (
            <>
              <div className="flex items-center gap-1.5">
                <span className="font-medium text-emerald-600">{t('common:x3.connectedColon', 'Verbunden:')}</span>
                <span>{result.connected ? 'Ja' : 'Nein'}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="font-medium text-emerald-600">{t('common:x3.authenticatedColon', 'Authentifiziert:')}</span>
                <span>{result.authenticated ? 'Ja' : 'Nein'}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="font-medium text-emerald-600">{t('common:x3.serverColon', 'Server:')}</span>
                <span>{result.details?.host}:{result.details?.port}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="font-medium text-emerald-600">{t('common:x3.durationColon', 'Dauer:')}</span>
                <span>{result.duration}</span>
              </div>
            </>
          ) : (
            <div className="text-rose-600">{result.error}</div>
          )}
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="flex items-center justify-center py-8 text-red-500">
        <AlertTriangle className="h-8 w-8 mr-2" />
        <div>
          <div className="font-medium">{t('common:state.errorOccurred', 'Fehler aufgetreten!')}</div>
          <div className="text-sm">{error?.message || 'Unbekannter Fehler'}</div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">{t('settings:tbl.loadingOrgs', 'Organisationen werden geladen...')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-8 font-sans">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-light text-slate-900 tracking-tight">{t('settings:tbl.orgs', 'Organisationen')}</h2>
          <p className="text-sm text-slate-500 mt-1">
             {t('settings:tbl.orgsHint', 'Mandanten und E-Mail-Infrastruktur zentral verwalten')}
          </p>
        </div>
      </div>

      <Card className="border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div className="space-y-1">
             <div className="flex flex-wrap items-center gap-2">
               <h3 className="text-base font-semibold text-slate-900">{t('common:field.overview', 'Übersicht')}</h3>
               <span className="text-xs text-slate-400 font-normal ml-2">({activeCount} aktiv)</span>
             </div>
             <p className="text-sm text-slate-500">
               {t('settings:tbl.orgsManageHint', 'Verwalten Sie hier alle angeschlossenen Mandanten und deren Mailserver-Verbindungen.')}
             </p>
          </div>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-9 gap-2 text-slate-600 hover:text-slate-900 border-slate-200"
              >
                <Plus className="h-4 w-4" />
                {t('common:x3.addOrganization', 'Organisation hinzufügen')}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[960px] p-0 overflow-hidden border-0 rounded-2xl max-h-[85vh] flex flex-col">
              {/* ... (Dialog content remains same structure but checked for styles) ... */}
                <div className="flex flex-1 overflow-hidden">
                  <div className="hidden md:flex flex-col gap-4 border-r border-slate-100 bg-slate-50 px-6 py-6 w-[280px] flex-shrink-0">
                    <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                      Onboarding
                    </div>
                    <p className="text-sm text-slate-600">
                      Hinterlegen Sie hier die Mandanten-Daten sowie IMAP/SMTP-Zugänge, damit Suppio E-Mails sicher abrufen und versenden kann.
                    </p>
                    <div className="space-y-3 text-sm text-slate-500 mt-auto">
                      <div className="rounded-xl bg-white p-4 border border-slate-100">
                        <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-2">
                          {t('common:x1.tips', 'Tipps')}
                        </p>
                        <ul className="space-y-1.5 text-xs text-slate-600">
                          <li>• Verwenden Sie Funktionspostfächer als Benutzer</li>
                          <li>• Aktivieren Sie SSL/TLS sofern der Provider es unterstützt</li>
                          <li>• Testen Sie den IMAP-Zugriff vor dem Speichern</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col flex-1 bg-white min-w-0">
                    <DialogHeader className="px-6 py-4 border-b border-slate-100 flex-shrink-0 text-left">
                      <DialogTitle className="text-base font-semibold text-slate-900">
                        {t('common:x3.newOrganization', 'Neue Organisation')}
                      </DialogTitle>
                      <DialogDescription className="text-xs text-slate-500 mt-1">
                        {t('common:x3.createOrgWithEmail', 'Erstellen Sie eine Organisation mit zugehöriger E-Mail-Konfiguration.')}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="flex-1 overflow-y-auto p-6">
                      <div className="space-y-6 max-w-2xl">
                        <div className="grid gap-4 md:grid-cols-2">
                          <div className="space-y-2">
                            <Label htmlFor="org-name" className="text-xs font-medium text-slate-700">{t('common:x1.nameReq', 'Name *')}</Label>
                            <Input
                              id="org-name"
                              value={newOrgData.name}
                              onChange={(e) => setNewOrgData(prev => ({ ...prev, name: e.target.value }))}
                              placeholder={t('settings:p2.companyName', 'Firmenname')}
                              className="h-9 border-slate-200 focus-visible:ring-slate-200"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="org-domain" className="text-xs font-medium text-slate-700">{t('common:field.domain', 'Domain')}</Label>
                            <Input
                              id="org-domain"
                              value={newOrgData.domain}
                              onChange={(e) => setNewOrgData(prev => ({ ...prev, domain: e.target.value }))}
                              placeholder="example.com"
                              className="h-9 border-slate-200 focus-visible:ring-slate-200"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="support-email" className="text-xs font-medium text-slate-700">{t('common:x3.supportEmailReq', 'Support E-Mail *')}</Label>
                          <Input
                            id="support-email"
                            type="email"
                            value={newOrgData.support_email}
                            onChange={(e) => setNewOrgData(prev => ({ ...prev, support_email: e.target.value }))}
                            placeholder="support@example.com"
                            className="h-9 border-slate-200 focus-visible:ring-slate-200"
                          />
                        </div>

                        {/* Lager (Warehouse) — bu org bir depo mailbox'ıdır; depo çalışanları basit UI ile görür */}
                        <div className="flex items-center justify-between rounded-xl border border-orange-200 bg-orange-50 px-3 py-2.5">
                          <div>
                            <Label className="text-sm font-semibold text-orange-800">{t('common:x3.warehouseQ', 'Lager (Warehouse)?')}</Label>
                            <p className="text-[11px] text-orange-700/80">{t('errors:fix.whMailboxHint', 'Lager-Postfach — Lager-Mitarbeiter sehen die einfache Oberfläche (kein Shop).')}</p>
                          </div>
                          <Switch checked={!!newOrgData.is_warehouse} onCheckedChange={(v) => setNewOrgData(prev => ({ ...prev, is_warehouse: v }))} />
                        </div>

                        <div className="space-y-4 rounded-xl border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5 p-4">
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                              <Mail className="h-4 w-4 text-slate-400" />
                              {t('common:x1.imapSettings', 'IMAP-Einstellungen')}
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-8 gap-1.5 text-xs border-slate-200"
                              onClick={handleTestNewOrgIMAP}
                              disabled={testIMAPConnection.isPending}
                            >
                              {testIMAPConnection.isPending ? (
                                <>
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  <span className="ml-1">{t('common:state.testing', 'Test läuft…')}</span>
                                </>
                              ) : (
                                <>
                                  <FlaskConical className="h-3.5 w-3.5" />
                                  <span className="ml-1">{t('common:action.test', 'Testen')}</span>
                                </>
                              )}
                            </Button>
                          </div>
                          <TestResult testKey="create-imap" className="md:max-w-md" />
                          <div className="grid gap-4 md:grid-cols-2">
                            <div className="space-y-2">
                              <Label className="text-[10px] uppercase font-bold text-slate-400">{t('common:field.host', 'Host')}</Label>
                              <Input
                                value={newOrgData.email_config.imap.host}
                                onChange={(e) => setNewOrgData(prev => ({
                                  ...prev,
                                  email_config: {
                                    ...prev.email_config,
                                    imap: { ...prev.email_config.imap, host: e.target.value }
                                  }
                                }))}
                                placeholder="imap.mailprovider.de"
                                className="h-8 text-xs"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-[10px] uppercase font-bold text-slate-400">{t('common:field.port', 'Port')}</Label>
                              <Input
                                type="number"
                                value={newOrgData.email_config.imap.port}
                                onChange={(e) => setNewOrgData(prev => ({
                                  ...prev,
                                  email_config: {
                                    ...prev.email_config,
                                    imap: { ...prev.email_config.imap, port: parseInt(e.target.value) || 993 }
                                  }
                                }))}
                                className="h-8 text-xs"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-[10px] uppercase font-bold text-slate-400">{t('common:field.users', 'Benutzer')}</Label>
                              <Input
                                type="email"
                                value={newOrgData.email_config.imap.user}
                                onChange={(e) => setNewOrgData(prev => ({
                                  ...prev,
                                  email_config: {
                                    ...prev.email_config,
                                    imap: { ...prev.email_config.imap, user: e.target.value }
                                  }
                                }))}
                                placeholder="postfach@example.com"
                                className="h-8 text-xs"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-[10px] uppercase font-bold text-slate-400">{t('common:field.password', 'Passwort')}</Label>
                              <div className="relative">
                                <Input
                                  type={showPasswords['new-imap'] ? 'text' : 'password'}
                                  value={newOrgData.email_config.imap.password}
                                  onChange={(e) => setNewOrgData(prev => ({
                                    ...prev,
                                    email_config: {
                                      ...prev.email_config,
                                      imap: { ...prev.email_config.imap, password: e.target.value }
                                    }
                                  }))}
                                  className="h-8 text-xs pr-8"
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="absolute right-1 top-1 h-6 w-6 p-0 text-slate-400 hover:text-slate-600"
                                  onClick={() => togglePasswordVisibility('new-imap')}
                                >
                                  {showPasswords['new-imap'] ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                                </Button>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 pt-1">
                            <Switch
                              checked={newOrgData.email_config.imap.secure}
                              onCheckedChange={(checked) => setNewOrgData(prev => ({
                                ...prev,
                                email_config: {
                                  ...prev.email_config,
                                  imap: { ...prev.email_config.imap, secure: checked }
                                }
                              }))}
                              className="scale-75 data-[state=checked]:bg-slate-900 origin-left"
                            />
                            <span className="text-xs text-slate-600">{t('common:x1.useSsl', 'SSL/TLS verwenden')}</span>
                          </div>
                        </div>

                        <div className="space-y-4 rounded-xl border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5 p-4">
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                              <Server className="h-4 w-4 text-slate-400" />
                              {t('common:x1.smtpSettings', 'SMTP-Einstellungen')}
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-8 gap-1.5 text-xs border-slate-200"
                              onClick={handleTestNewOrgSMTP}
                              disabled={testSMTPConnection.isPending}
                            >
                              {testSMTPConnection.isPending ? (
                                <>
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  <span className="ml-1">{t('common:state.testing', 'Test läuft…')}</span>
                                </>
                              ) : (
                                <>
                                  <FlaskConical className="h-3.5 w-3.5" />
                                  <span className="ml-1">{t('common:action.test', 'Testen')}</span>
                                </>
                              )}
                            </Button>
                          </div>
                          <TestResult testKey="create-smtp" className="md:max-w-md" />
                          <div className="grid gap-4 md:grid-cols-2">
                            <div className="space-y-2">
                              <Label className="text-[10px] uppercase font-bold text-slate-400">{t('common:field.host', 'Host')}</Label>
                              <Input
                                value={newOrgData.email_config.smtp.host}
                                onChange={(e) => setNewOrgData(prev => ({
                                  ...prev,
                                  email_config: {
                                    ...prev.email_config,
                                    smtp: { ...prev.email_config.smtp, host: e.target.value }
                                  }
                                }))}
                                placeholder="smtp.mailprovider.de"
                                className="h-8 text-xs"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-[10px] uppercase font-bold text-slate-400">{t('common:field.port', 'Port')}</Label>
                              <Input
                                type="number"
                                value={newOrgData.email_config.smtp.port}
                                onChange={(e) => setNewOrgData(prev => ({
                                  ...prev,
                                  email_config: {
                                    ...prev.email_config,
                                    smtp: { ...prev.email_config.smtp, port: parseInt(e.target.value) || 587 }
                                  }
                                }))}
                                className="h-8 text-xs"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-[10px] uppercase font-bold text-slate-400">{t('common:field.users', 'Benutzer')}</Label>
                              <Input
                                type="email"
                                value={newOrgData.email_config.smtp.user}
                                onChange={(e) => setNewOrgData(prev => ({
                                  ...prev,
                                  email_config: {
                                    ...prev.email_config,
                                    smtp: { ...prev.email_config.smtp, user: e.target.value }
                                  }
                                }))}
                                placeholder="postfach@example.com"
                                className="h-8 text-xs"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-[10px] uppercase font-bold text-slate-400">{t('common:field.password', 'Passwort')}</Label>
                              <div className="relative">
                                <Input
                                  type={showPasswords['new-smtp'] ? 'text' : 'password'}
                                  value={newOrgData.email_config.smtp.password}
                                  onChange={(e) => setNewOrgData(prev => ({
                                    ...prev,
                                    email_config: {
                                      ...prev.email_config,
                                      smtp: { ...prev.email_config.smtp, password: e.target.value }
                                    }
                                  }))}
                                  className="h-8 text-xs pr-8"
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="absolute right-1 top-1 h-6 w-6 p-0 text-slate-400 hover:text-slate-600"
                                  onClick={() => togglePasswordVisibility('new-smtp')}
                                >
                                  {showPasswords['new-smtp'] ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                                </Button>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 pt-1">
                            <Switch
                              checked={newOrgData.email_config.smtp.secure}
                              onCheckedChange={(checked) => setNewOrgData(prev => ({
                                ...prev,
                                email_config: {
                                  ...prev.email_config,
                                  smtp: { ...prev.email_config.smtp, secure: checked }
                                }
                              }))}
                              className="scale-75 data-[state=checked]:bg-slate-900 origin-left"
                            />
                            <span className="text-xs text-slate-600">{t('common:x1.useSsl', 'SSL/TLS verwenden')}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <DialogFooter className="flex flex-col gap-2 border-t border-slate-100 bg-slate-50/50 px-6 py-4 md:flex-row md:justify-end flex-shrink-0">
                      <Button
                        variant="outline"
                        onClick={() => setIsCreateDialogOpen(false)}
                        className="h-9 rounded-lg border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-white"
                      >
                        {t('common:action.cancel', 'Abbrechen')}
                      </Button>
                      <Button
                        onClick={handleCreateOrganization}
                        disabled={createOrganization.isPending}
                        className="h-9 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
                      >
                        {createOrganization.isPending ? (
                          <span className="flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            {t('common:x3.creatingDots', 'Wird erstellt…')}
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            {t('common:action.save', 'Speichern')}
                          </span>
                        )}
                      </Button>
                    </DialogFooter>
                  </div>
                </div>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          <OrganizationDataTable
            organizations={organizations}
            isLoading={isLoading}
            testingIds={testingOrgs}
            onTest={handleTestIMAP}
            onEdit={(org) => {
              const editPayload: UpdateOrganizationData = {
                name: org.name,
                domain: org.domain,
                support_email: org.support_email,
                is_active: org.is_active,
                email_config: org.email_config
                  ? {
                      imap: {
                        host: org.email_config.imap?.host || '',
                        port: org.email_config.imap?.port || 993,
                        secure: org.email_config.imap?.secure !== false,
                        user: org.email_config.imap?.user || '',
                        password: org.email_config.imap?.password ?? '',
                      },
                      smtp: {
                        host: org.email_config.smtp?.host || '',
                        port: org.email_config.smtp?.port || 587,
                        secure: org.email_config.smtp?.secure || false,
                        user: org.email_config.smtp?.user || '',
                        password: org.email_config.smtp?.password ?? '',
                      },
                    }
                  : undefined,
              };

              setEditingOrg(org);
              setEditData(editPayload);
              setIsEditDialogOpen(true);
            }}
            onDelete={handleDeleteOrganization}
            isDeletePending={deleteOrganization.isPending}
          />
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[960px] p-0 overflow-hidden border-0 rounded-2xl max-h-[85vh] flex flex-col">
          {editingOrg ? (
            <div className="flex flex-1 overflow-hidden">
              <div className="hidden md:flex flex-col gap-4 border-r border-slate-100 bg-slate-50 px-6 py-6 w-[280px] flex-shrink-0">
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                  {t('common:field.organization', 'Organisation')}
                </div>
                <div className="rounded-xl bg-white p-4 border border-slate-100">
                  <p className="text-sm font-semibold text-slate-900">{editingOrg.name}</p>
                  {editingOrg.support_email && (
                    <p className="mt-1 text-xs text-slate-500">{editingOrg.support_email}</p>
                  )}
                  {editingOrg.domain && (
                    <p className="mt-1 text-xs text-slate-400">{editingOrg.domain}</p>
                  )}
                </div>
                <div className="rounded-xl bg-white p-4 border border-slate-100">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-2">
                    {t('common:field.status', 'Status')}
                  </p>
                  <p className={`text-sm font-medium ${editingOrg.is_active ? 'text-emerald-600' : 'text-slate-500'}`}>
                    {editingOrg.is_active ? 'Aktiv' : 'Inaktiv'}
                  </p>
                  <p className="mt-2 text-[10px] text-slate-400">
                    {t('common:x3.lastUpdatedColon', 'Zuletzt aktualisiert:')} {formatDate(editingOrg.updated_at ?? editingOrg.created_at ?? Date.now())}
                  </p>
                </div>
                <div className="rounded-xl bg-white p-4 border border-slate-100 mt-auto">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-2">
                    {t('common:x3.hint', 'Hinweis')}
                  </p>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    Ändern Sie Zugangsdaten nur, wenn der Mail-Provider sie aktualisiert hat. Testen Sie im Anschluss die Verbindung.
                  </p>
                </div>
              </div>
              <div className="flex flex-col flex-1 bg-white min-w-0">
                <DialogHeader className="px-6 py-4 border-b border-slate-100 flex-shrink-0 text-left">
                  <DialogTitle className="text-base font-semibold text-slate-900">
                    {t('common:x3.editOrganization', 'Organisation bearbeiten')}
                  </DialogTitle>
                  <DialogDescription className="text-xs text-slate-500 mt-1">
                    {t('common:x3.updateOrgMasterData', 'Aktualisieren Sie Stammdaten und E-Mail-Konfiguration von')} {editingOrg.name}.
                  </DialogDescription>
                </DialogHeader>
                <div className="flex-1 overflow-y-auto p-6">
                  <div className="space-y-6 max-w-2xl">
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-slate-700">{t('common:field.name', 'Name')}</Label>
                        <Input
                          value={editData.name || ''}
                          onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))}
                           className="h-9 border-slate-200 focus-visible:ring-slate-200"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-slate-700">{t('common:field.domain', 'Domain')}</Label>
                        <Input
                          value={editData.domain || ''}
                          onChange={(e) => setEditData(prev => ({ ...prev, domain: e.target.value }))}
                          placeholder="example.com"
                           className="h-9 border-slate-200 focus-visible:ring-slate-200"
                        />
                      </div>
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-slate-700">{t('common:x3.supportEmail', 'Support E-Mail')}</Label>
                        <Input
                          type="email"
                          value={editData.support_email || ''}
                          onChange={(e) => setEditData(prev => ({ ...prev, support_email: e.target.value }))}
                          placeholder="support@example.com"
                           className="h-9 border-slate-200 focus-visible:ring-slate-200"
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 rounded-lg border border-slate-100 bg-slate-50/50 h-[60px] mt-auto">
                        <div>
                          <p className="text-sm font-medium text-slate-700">{t('common:x3.orgActive', 'Organisation aktiv')}</p>
                          <p className="text-[10px] text-slate-400">{t('common:x3.allowMailboxAccess', 'Postfach-Zugriff erlauben')}</p>
                        </div>
                        <Switch
                          checked={editData.is_active !== false}
                          onCheckedChange={(checked) => setEditData(prev => ({ ...prev, is_active: checked }))}
                          className="data-[state=checked]:bg-emerald-500"
                        />
                      </div>
                    </div>

                    <div className="space-y-4 rounded-xl border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5 p-4">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                          <Mail className="h-4 w-4 text-slate-400" />
                          {t('common:x1.imapSettings', 'IMAP-Einstellungen')}
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 gap-1.5 text-xs border-slate-200"
                          onClick={() => {
                            if (editData.email_config?.imap) {
                              handleIMAPTest(editData.email_config.imap, `edit-imap-${editingOrg?.id}`);
                            }
                          }}
                          disabled={
                            testIMAPConnection.isPending ||
                            !editData.email_config?.imap?.host ||
                            !editData.email_config?.imap?.user ||
                            !editData.email_config?.imap?.password
                          }
                        >
                          {testIMAPConnection.isPending ? (
                            <>
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              <span className="ml-1">{t('common:state.testing', 'Test läuft…')}</span>
                            </>
                          ) : (
                            <>
                              <FlaskConical className="h-3.5 w-3.5" />
                              <span className="ml-1">{t('common:action.test', 'Testen')}</span>
                            </>
                          )}
                        </Button>
                      </div>
                      <TestResult testKey={`edit-imap-${editingOrg?.id}`} className="md:max-w-md" />
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label className="text-[10px] uppercase font-bold text-slate-400">{t('common:field.host', 'Host')}</Label>
                          <Input
                            value={editData.email_config?.imap?.host || ''}
                            onChange={(e) => setEditData(prev => ({
                              ...prev,
                              email_config: {
                                ...prev.email_config,
                                imap: { 
                                  ...prev.email_config?.imap, 
                                  host: e.target.value,
                                  port: prev.email_config?.imap?.port || 993,
                                  secure: prev.email_config?.imap?.secure !== false,
                                  user: prev.email_config?.imap?.user || '',
                                  password: prev.email_config?.imap?.password ?? ''
                                }
                              }
                            }))}
                            placeholder="imap.mailprovider.de"
                            className="h-8 text-xs"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[10px] uppercase font-bold text-slate-400">{t('common:field.port', 'Port')}</Label>
                          <Input
                            type="number"
                            value={editData.email_config?.imap?.port || 993}
                            onChange={(e) => setEditData(prev => ({
                              ...prev,
                              email_config: {
                                ...prev.email_config,
                                imap: { 
                                  ...prev.email_config?.imap, 
                                  port: parseInt(e.target.value) || 993,
                                  host: prev.email_config?.imap?.host || '',
                                  secure: prev.email_config?.imap?.secure !== false,
                                  user: prev.email_config?.imap?.user || '',
                                  password: prev.email_config?.imap?.password ?? ''
                                }
                              }
                            }))}
                            className="h-8 text-xs"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[10px] uppercase font-bold text-slate-400">{t('common:field.users', 'Benutzer')}</Label>
                          <Input
                            type="email"
                            value={editData.email_config?.imap?.user || ''}
                            onChange={(e) => setEditData(prev => ({
                              ...prev,
                              email_config: {
                                ...prev.email_config,
                                imap: { 
                                  ...prev.email_config?.imap, 
                                  user: e.target.value,
                                  host: prev.email_config?.imap?.host || '',
                                  port: prev.email_config?.imap?.port || 993,
                                  secure: prev.email_config?.imap?.secure !== false,
                                  password: prev.email_config?.imap?.password ?? ''
                                }
                              }
                            }))}
                            placeholder="postfach@example.com"
                            className="h-8 text-xs"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[10px] uppercase font-bold text-slate-400">{t('common:field.password', 'Passwort')}</Label>
                          <div className="relative">
                            <Input
                              type={showPasswords[`edit-imap-${editingOrg?.id}`] ? 'text' : 'password'}
                              value={editData.email_config?.imap?.password ?? ''}
                              onChange={(e) => setEditData(prev => ({
                                ...prev,
                                email_config: {
                                  ...prev.email_config,
                                  imap: { 
                                    ...prev.email_config?.imap, 
                                    password: e.target.value,
                                    host: prev.email_config?.imap?.host || '',
                                    port: prev.email_config?.imap?.port || 993,
                                    secure: prev.email_config?.imap?.secure !== false,
                                    user: prev.email_config?.imap?.user || ''
                                  }
                                }
                              }))}
                              className="h-8 text-xs pr-8"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="absolute right-1 top-1 h-6 w-6 p-0 text-slate-400 hover:text-slate-600"
                              onClick={() => togglePasswordVisibility(`edit-imap-${editingOrg?.id}`)}
                            >
                              {showPasswords[`edit-imap-${editingOrg?.id}`] ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                            </Button>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 pt-1">
                        <Switch
                          checked={editData.email_config?.imap?.secure !== false}
                          onCheckedChange={(checked) => setEditData(prev => ({
                            ...prev,
                            email_config: {
                              ...prev.email_config,
                              imap: { 
                                ...prev.email_config?.imap, 
                                secure: checked,
                                host: prev.email_config?.imap?.host || '',
                                port: prev.email_config?.imap?.port || 993,
                                user: prev.email_config?.imap?.user || '',
                                password: prev.email_config?.imap?.password || ''
                              }
                            }
                          }))}
                          className="scale-75 data-[state=checked]:bg-slate-900 origin-left"
                        />
                        <span className="text-xs text-slate-600">{t('common:x1.useSsl', 'SSL/TLS verwenden')}</span>
                      </div>
                    </div>

                    <div className="space-y-4 rounded-xl border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5 p-4">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                          <Server className="h-4 w-4 text-slate-400" />
                          {t('common:x1.smtpSettings', 'SMTP-Einstellungen')}
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 gap-1.5 text-xs border-slate-200"
                          onClick={handleTestEditOrgSMTP}
                          disabled={testSMTPConnection.isPending}
                        >
                          {testSMTPConnection.isPending ? (
                            <>
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              <span className="ml-1">{t('common:state.testing', 'Test läuft…')}</span>
                            </>
                          ) : (
                            <>
                              <FlaskConical className="h-3.5 w-3.5" />
                              <span className="ml-1">{t('common:action.test', 'Testen')}</span>
                            </>
                          )}
                        </Button>
                      </div>
                      <TestResult testKey={`edit-smtp-${editingOrg?.id}`} className="md:max-w-md" />
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label className="text-[10px] uppercase font-bold text-slate-400">{t('common:field.host', 'Host')}</Label>
                          <Input
                            value={editData.email_config?.smtp?.host || ''}
                            onChange={(e) => setEditData(prev => ({
                              ...prev,
                              email_config: {
                                ...prev.email_config,
                                smtp: {
                                  ...prev.email_config?.smtp,
                                  host: e.target.value,
                                  port: prev.email_config?.smtp?.port || 587,
                                  secure: prev.email_config?.smtp?.secure || false,
                                  user: prev.email_config?.smtp?.user || '',
                                  password: prev.email_config?.smtp?.password ?? ''
                                }
                              }
                            }))}
                            placeholder="smtp.mailprovider.de"
                            className="h-8 text-xs"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[10px] uppercase font-bold text-slate-400">{t('common:field.port', 'Port')}</Label>
                          <Input
                            type="number"
                            value={editData.email_config?.smtp?.port || 587}
                            onChange={(e) => setEditData(prev => ({
                              ...prev,
                              email_config: {
                                ...prev.email_config,
                                smtp: { 
                                  ...prev.email_config?.smtp, 
                                  port: parseInt(e.target.value) || 587,
                                  host: prev.email_config?.smtp?.host || '',
                                  secure: prev.email_config?.smtp?.secure || false,
                                  user: prev.email_config?.smtp?.user || '',
                                  password: prev.email_config?.smtp?.password ?? ''
                                }
                              }
                            }))}
                            className="h-8 text-xs"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[10px] uppercase font-bold text-slate-400">{t('common:field.users', 'Benutzer')}</Label>
                          <Input
                            type="email"
                            value={editData.email_config?.smtp?.user || ''}
                            onChange={(e) => setEditData(prev => ({
                              ...prev,
                              email_config: {
                                ...prev.email_config,
                                smtp: { 
                                  ...prev.email_config?.smtp, 
                                  user: e.target.value,
                                  host: prev.email_config?.smtp?.host || '',
                                  port: prev.email_config?.smtp?.port || 587,
                                  secure: prev.email_config?.smtp?.secure || false,
                                  password: prev.email_config?.smtp?.password ?? ''
                                }
                              }
                            }))}
                            placeholder="postfach@example.com"
                            className="h-8 text-xs"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[10px] uppercase font-bold text-slate-400">{t('common:field.password', 'Passwort')}</Label>
                          <div className="relative">
                            <Input
                              type={showPasswords[`edit-smtp-${editingOrg?.id}`] ? 'text' : 'password'}
                              value={editData.email_config?.smtp?.password ?? ''}
                              onChange={(e) => setEditData(prev => ({
                                ...prev,
                                email_config: {
                                  ...prev.email_config,
                                  smtp: { 
                                    ...prev.email_config?.smtp, 
                                    password: e.target.value,
                                    host: prev.email_config?.smtp?.host || '',
                                    port: prev.email_config?.smtp?.port || 587,
                                    secure: prev.email_config?.smtp?.secure || false,
                                    user: prev.email_config?.smtp?.user || ''
                                  }
                                }
                              }))}
                              className="h-8 text-xs pr-8"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="absolute right-1 top-1 h-6 w-6 p-0 text-slate-400 hover:text-slate-600"
                              onClick={() => togglePasswordVisibility(`edit-smtp-${editingOrg?.id}`)}
                            >
                              {showPasswords[`edit-smtp-${editingOrg?.id}`] ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                            </Button>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 pt-1">
                        <Switch
                          checked={editData.email_config?.smtp?.secure || false}
                          onCheckedChange={(checked) => setEditData(prev => ({
                            ...prev,
                            email_config: {
                              ...prev.email_config,
                              smtp: { 
                                ...prev.email_config?.smtp, 
                                secure: checked,
                                host: prev.email_config?.smtp?.host || '',
                                port: prev.email_config?.smtp?.port || 587,
                                user: prev.email_config?.smtp?.user || '',
                                password: prev.email_config?.smtp?.password || ''
                              }
                            }
                          }))}
                          className="scale-75 data-[state=checked]:bg-slate-900 origin-left"
                        />
                        <span className="text-xs text-slate-600">{t('common:x1.useSsl', 'SSL/TLS verwenden')}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <DialogFooter className="flex flex-col gap-2 border-t border-slate-100 bg-slate-50/50 px-6 py-4 md:flex-row md:justify-end flex-shrink-0">
                  <Button
                    variant="outline"
                    onClick={() => setIsEditDialogOpen(false)}
                    className="h-9 rounded-lg border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-white"
                  >
                    {t('common:action.cancel', 'Abbrechen')}
                  </Button>
                  <Button
                    onClick={handleUpdateOrganization}
                    disabled={updateOrganization.isPending}
                    className="h-9 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
                  >
                    {updateOrganization.isPending ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {t('common:x3.savingDots2', 'Speichert…')}
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        {t('common:action.save', 'Speichern')}
                      </span>
                    )}
                  </Button>
                </DialogFooter>
              </div>
            </div>
          ) : (
            <div className="px-6 py-10 text-center text-sm text-slate-500">
              {t('common:x3.noOrgSelected', 'Keine Organisation ausgewählt.')}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
